import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import Seo from '../components/Seo';
import { Check, Clock, Zap, Sparkles, Calendar, HelpCircle } from 'lucide-react';
import { CONTACT } from '../mock';

const CONCERNS = [
  'PTSD and Complex PTSD',
  'Childhood trauma',
  'Anxiety and panic attacks',
  'Grief and loss',
  'First responders',
  'Military and veterans',
  'Medical trauma',
  'Relationship trauma',
  'Narcissistic abuse recovery',
  'Phobias',
  'Disturbing memories',
  'Performance anxiety',
];

const PACKAGES = [
  {
    tier: '2-Hour Intensive',
    price: 'Starting at $350',
    text: 'Focused, targeted work on a specific memory or trigger.',
  },
  {
    tier: '3-Hour Intensive',
    price: 'Starting at $500',
    text: 'A half-day option — ideal for one specific traumatic event or accelerating progress already being made in therapy.',
  },
  {
    tier: '4-Hour Intensive',
    price: 'Starting at $650',
    text: 'Extended focused work with additional grounding, integration, and aftercare planning.',
  },
  {
    tier: 'Full-Day Intensive',
    price: 'Starting at $950',
    text: 'Approximately 6 hours — designed for processing more significant trauma or making substantial progress in a single day. Includes scheduled breaks and lunch.',
    highlight: true,
  },
];

const FAQ = [
  {
    q: 'Does insurance cover Trauma Intensives?',
    a: 'Because trauma intensives involve extended sessions that differ from traditional psychotherapy, they are generally considered a private-pay service. We can provide a superbill for potential out-of-network reimbursement, depending on your insurance plan.',
  },
  {
    q: 'How many sessions will I need?',
    a: 'Some individuals experience significant relief after one intensive, while others choose to continue with ongoing therapy for continued growth and support.',
  },
  {
    q: 'Do I have to be an existing client?',
    a: 'No. Following a consultation and assessment, trauma intensives are available for both new and existing clients if clinically appropriate.',
  },
];

const EXPECT = [
  'Comprehensive assessment and treatment planning',
  'Preparation and grounding techniques',
  'EMDR and/or Accelerated Resolution Therapy',
  'Breaks throughout the day',
  'Personalized coping tools',
  'Follow-up recommendations and aftercare planning',
];

export default function TraumaIntensives() {
  return (
    <>
      <Seo
        title="EMDR & ART Trauma Intensives in Chesapeake, VA"
        description="Accelerated trauma healing through EMDR and Accelerated Resolution Therapy (ART) intensives in Chesapeake, VA. Half-day and full-day packages for PTSD, childhood trauma, first responders, and veterans."
        path="/trauma-intensives"
      />
      <PageHero
        eyebrow="Trauma Intensive Therapy"
        title="Accelerated Healing"
        highlight="EMDR & ART Intensives"
        subtitle="For those ready to make significant progress in a focused, personalized format — serving Chesapeake, Virginia Beach, Norfolk, Suffolk, and Hampton Roads."
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Services', to: '/services' }, { label: 'Trauma Intensives' }]}
      />

      {/* Intro */}
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 reveal">
          <div className="text-center mb-8">
            <div className="eyebrow mb-3">What Is a Trauma Intensive?</div>
            <h2 className="section-title mb-4">
              Some wounds require <span className="text-[color:var(--dusty-rose)]">more than</span> a 60-minute session.
            </h2>
          </div>
          <div className="space-y-4 text-[color:var(--taupe-gray)] leading-relaxed text-lg">
            <p>
              Our Trauma Intensive Program is designed for individuals who are ready to make a commitment and significant progress in a focused, personalized format. Rather than spreading treatment over many months, trauma intensives allow you to dedicate uninterrupted time to your healing — often leading to meaningful breakthroughs in a much shorter period.
            </p>
            <p>
              Using evidence-based approaches such as <strong className="text-[color:var(--deep-sage)]">EMDR (Eye Movement Desensitization and Reprocessing)</strong> and <strong className="text-[color:var(--deep-sage)]">Accelerated Resolution Therapy (ART)</strong>, we help clients process traumatic experiences, reduce emotional distress, and create lasting change.
            </p>
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="section-pad" style={{ background: 'var(--cream)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Who It Helps</div>
            <h2 className="section-title mb-3">
              Trauma Intensives may be <span className="text-[color:var(--dusty-rose)]">beneficial for</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 stagger">
            {CONCERNS.map((c) => (
              <div key={c} className="card-soft px-5 py-4 flex items-center gap-3 reveal">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(232,165,169,0.18)' }}>
                  <Sparkles className="w-4 h-4 text-[color:var(--dusty-rose)]" strokeWidth={1.6} />
                </div>
                <span className="text-sm text-[color:var(--deep-sage)] font-medium">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-10 items-start">
          <div className="reveal">
            <div className="eyebrow mb-3">Personalized Care</div>
            <h2 className="section-title mb-4">
              What to <span className="text-[color:var(--dusty-rose)]">Expect</span>
            </h2>
            <p className="text-[color:var(--taupe-gray)] leading-relaxed mb-6">
              Every intensive is customized to your individual needs. A pre-intensive consultation ensures the format and pacing are clinically appropriate and likely to be beneficial.
            </p>
          </div>
          <ul className="space-y-3 reveal">
            {EXPECT.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(184,190,164,0.28)' }}>
                  <Check className="w-3.5 h-3.5 text-[color:var(--sage-dark)]" strokeWidth={2.5} />
                </div>
                <span className="text-[color:var(--deep-sage)]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Packages */}
      <section className="section-pad" style={{ background: 'var(--cream)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Investment</div>
            <h2 className="section-title mb-3">
              Trauma Intensive <span className="text-[color:var(--dusty-rose)]">Packages</span>
            </h2>
            <p className="text-[color:var(--taupe-gray)] max-w-2xl mx-auto">
              Customized full-day intensives available. Every package begins with a consultation to ensure clinical fit.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 stagger">
            {PACKAGES.map((p) => (
              <div
                key={p.tier}
                className={`card-soft p-6 reveal ${p.highlight ? 'ring-2 ring-[color:var(--dusty-rose)]' : ''}`}
                style={p.highlight ? { background: 'linear-gradient(135deg, rgba(232,165,169,0.10), rgba(184,190,164,0.14))' } : {}}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'rgba(232,165,169,0.18)' }}>
                  {p.highlight ? <Zap className="w-5 h-5 text-[color:var(--dusty-rose)]" strokeWidth={1.6} /> : <Clock className="w-5 h-5 text-[color:var(--dusty-rose)]" strokeWidth={1.6} />}
                </div>
                <div className="font-serif-display text-lg text-[color:var(--deep-sage)] font-bold">{p.tier}</div>
                <div className="text-[color:var(--dusty-rose)] font-serif-display text-2xl mt-1">{p.price}</div>
                <p className="text-sm text-[color:var(--taupe-gray)] leading-relaxed mt-3">{p.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 reveal">
            <Link to="/contact" className="btn-primary"><Calendar className="w-4 h-4" /> Request a Consultation</Link>
          </div>
        </div>
      </section>

      {/* Is it right for me */}
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 reveal">
          <div className="text-center mb-8">
            <div className="eyebrow mb-3">Right Fit?</div>
            <h2 className="section-title mb-3">
              Is a Trauma Intensive <span className="text-[color:var(--dusty-rose)]">Right for You?</span>
            </h2>
          </div>
          <p className="text-[color:var(--taupe-gray)] leading-relaxed mb-6 text-center max-w-2xl mx-auto">A trauma intensive may be a great fit if you:</p>
          <ul className="grid sm:grid-cols-2 gap-3">
            {[
              'Have limited time for weekly therapy',
              'Are traveling from outside the area',
              'Feel “stuck” in traditional therapy',
              'Want focused treatment before returning to work or school',
              'Prefer making meaningful progress over a short period of time',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 card-soft p-4">
                <Check className="w-4 h-4 text-[color:var(--sage-dark)] mt-1 flex-shrink-0" strokeWidth={2.5} />
                <span className="text-sm text-[color:var(--deep-sage)]">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-[color:var(--taupe-gray)] italic text-center mt-6 max-w-2xl mx-auto">
            Every client completes a consultation beforehand to ensure that an intensive is clinically appropriate and likely to be beneficial.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" style={{ background: 'var(--cream)' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Common Questions</div>
            <h2 className="section-title mb-3">
              Frequently Asked <span className="text-[color:var(--dusty-rose)]">Questions</span>
            </h2>
          </div>
          <div className="space-y-4 stagger">
            {FAQ.map((f) => (
              <div key={f.q} className="card-soft p-6 reveal">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-[color:var(--dusty-rose)] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-serif-display text-lg text-[color:var(--deep-sage)] font-bold mb-2">{f.q}</h3>
                    <p className="text-[color:var(--taupe-gray)] leading-relaxed text-sm">{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 reveal">
            <p className="text-sm text-[color:var(--taupe-gray)] mb-4">
              Chesapeake Counseling Center is located in Chesapeake, Virginia and serves clients in Chesapeake, Virginia Beach, Norfolk, Suffolk, and surrounding Hampton Roads areas.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/schedule" className="btn-primary"><Calendar className="w-4 h-4" /> Book a Consultation</Link>
              <a href={CONTACT.phoneHref} className="btn-secondary">Or call {CONTACT.phone}</a>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
